import store from "@/store";
import { useModalStore } from "@/store/stores/modal";
import { ApiWrapper } from "../types/main";
import {
    CurrentTariff,
    PreviousTariff,
    IntegrationInstallPayload,
    MarketplaceInstallResult,
    MarketplaceIntegration,
    MarketplaceIntegrationFeedback, TariffData, DetailedIntegrationData,
} from "../types/marketplace";
import { apiCall, http } from "./http";
import type { SpecialPaymentLink } from "@/helpers/billing/specialPayment";

export interface SolvePaymentParams {
    projectId: string | string[]
    userId: string
    marketplaceIntegrationId: string
    subscriptionPeriod: string
    subscriptionId?: number
    renewalMode?: 'standalone' | 'with_tariff' | null
    extraChannels?: number
    keepAddonSubscriptionIds?: number[]
    extraIntegrationIds?: string[]
    effectiveMode?: 'immediate' | 'at_period_end'
}
export interface CreateFeedbackParams {
    projectId: string;
    integrationId: string;
    rating: number;
    comment: string;
}
export interface InstallIntegrationParams {
    projectId: string;
    integrationId: string;
    installPayload?: IntegrationInstallPayload;
    subscriptionPeriod?: string;
    paymentPayloadId?: number;
}
export interface GetIntegrationActivationListParams {
    projectId: string;
    integrationId: string;
}
export interface GetAutomailSettings {
    projectId: string;
    integrationId: string;
}

export interface DisableSubscriptionParams {
    projectId: string;
    subscriptionId: number;
}
export enum SubscriptionCancellationReason {
    WontUse = 'wont_use',
    PaymentMethod = 'payment_method',
    Other = 'other',
}
export interface ToggleSubscriptionParams {
    projectId: string;
    subscriptionId: number;
    status: 'enable' | 'disable'
    cancellationReason?: SubscriptionCancellationReason
}
export interface DisableIntegrationParams {
    projectId: string;
    installId: number;
}

export async function getAll(projectId: string): Promise<ApiWrapper<MarketplaceIntegration[]>> {
    const url = `marketplace/integration/all`;
    const res = apiCall<any>(() => http.get(url.toString(), { searchParams: { projectId } }));
    return res;
}
export async function getFeedback(integrationId: string) {
    const url = `marketplace/integration/feedback`;
    const res = apiCall<MarketplaceIntegrationFeedback[]>(() =>
        http.get(url.toString(), { searchParams: { integrationId } }),
    );
    return res;
}
export async function createFeedback(params: CreateFeedbackParams) {
    const url = `marketplace/integration/feedback`;
    const res = apiCall<MarketplaceIntegrationFeedback>(() => http.post(url.toString(), { json: params }));
    return res;
}
export async function installIntegration(params: InstallIntegrationParams) {
    const url = `marketplace/integration/install`;
    const res = apiCall<MarketplaceInstallResult>(() =>
        http.post(url.toString(), {
            json: {
                paymentPayloadId: params.paymentPayloadId,
                projectId: params.projectId,
                integrationId: params.integrationId,
                subscriptionPeriod: params.subscriptionPeriod,
                installPayload: params.installPayload,
            },
        }),
    );
    return res;
}
export async function uninstallIntegration(params: InstallIntegrationParams) {
    const url = `marketplace/integration/uninstall`;
    const res = apiCall<MarketplaceIntegrationFeedback>(() => http.post(url.toString(), { json: params }));
    return res;
}
export async function getIntegrationActivationList(params: GetIntegrationActivationListParams) {
    const url = `marketplace/integration/activationList`;
    const res = apiCall<any[]>(() =>
        http.get(url.toString(), {
            searchParams: { integrationId: params.integrationId, projectId: params.projectId },
        }),
    );
    return res;
}
export async function getAutomailSettings(params: GetAutomailSettings) {
    const url = `marketplace/integration/automailSettings`;
    const res = apiCall<any[]>(() =>
        http.get(url.toString(), {
            searchParams: { integrationId: params.integrationId, projectId: params.projectId },
        }),
    );
    return res;
}
export async function activateIntegrationTrial(params: { projectId: string; integrationId: string }) {
    const url = `marketplace/billing/activateIntegrationTrial`
    return apiCall<unknown>(() => http.post(url, { json: params }))
}
export async function getCurrentTariff(projectId: string) {
    const url = `marketplace/billing/currentTariff`
    const res = apiCall<{
        currentTariff: CurrentTariff | null
        previousTariff: PreviousTariff | null
        isTrialActivatedBefore?: boolean
    }>(() =>
        http.get(url.toString(), {
            searchParams: { projectId },
        }),
    );
    return res;
}

export async function getNotificationPhone(projectId: string) {
    const url = `marketplace/billing/getSavedNotificationPhone`;
    const res = apiCall<{phone: string}>(() =>
        http.get(url.toString(), {
            searchParams: { projectId },
        }),
    );
    return res;
}
export async function saveNotificationPhone({ projectId, phone }) {
    const url = `marketplace/billing/saveNotificationPhone`;
    const res = apiCall<{status: string}>(() =>
        http.post(url.toString(), {
            json: { projectId, phone },
        }),
    );
    return res;
}
export async function getAllTariffs(projectId: string) {
    const url = `marketplace/billing/tariffs`;
    const res = apiCall<TariffData>(() =>
        http.get(url.toString(), {
            searchParams: { projectId },
        }),
    );
    return res;
}
export async function solvePayment(params: SolvePaymentParams) {
    const url = `marketplace/billing/solvePayment`
    const searchParams: Record<string, any> = {
        projectId: Array.isArray(params.projectId)
            ? params.projectId.join(',')
            : params.projectId,
        userId: params.userId,
        marketplaceIntegrationId: params.marketplaceIntegrationId,
        subscriptionPeriod: params.subscriptionPeriod,
    }
    if (params.renewalMode !== undefined) {
        searchParams.renewalMode = params.renewalMode
    }
    if (params.extraChannels !== undefined) {
        searchParams.extraChannels = params.extraChannels
    }
    if (params.keepAddonSubscriptionIds !== undefined) {
        searchParams.keepAddonSubscriptionIds = params.keepAddonSubscriptionIds.join(',')
    }
    if (params.extraIntegrationIds?.length) {
        searchParams.extraIntegrationIds = params.extraIntegrationIds.join(',')
    }
    if (params.effectiveMode) {
        searchParams.effectiveMode = params.effectiveMode
    }
    const res = apiCall<MarketplaceIntegration[]>(() =>
        http.get(url.toString(), {
            searchParams,
        })
    )
    return res
}
export async function getSpecialPaymentOffer(params: { projectId: string; paymentPayloadId: number }) {
    const url = `marketplace/billing/paymentOffer`
    return apiCall<any>(() => http.get(url.toString(), { searchParams: params }))
}
export async function solveRenewalSubscription(params: SolvePaymentParams) {
    const url = `marketplace/billing/solveRenewalSubscription`;
    const searchParams: Record<string, any> = {
        projectId: Array.isArray(params.projectId)
            ? params.projectId.join(",")
            : params.projectId,
        userId: params.userId,
        subscriptionId: params.subscriptionId,
        marketplaceIntegrationId: params.marketplaceIntegrationId,
        subscriptionPeriod: params.subscriptionPeriod,
    }
    if (params.renewalMode !== undefined) {
        searchParams.renewalMode = params.renewalMode
    }
    if (params.extraChannels !== undefined) {
        searchParams.extraChannels = params.extraChannels
    }
    if (params.keepAddonSubscriptionIds !== undefined) {
        searchParams.keepAddonSubscriptionIds = params.keepAddonSubscriptionIds.join(',')
    }
    if (params.extraIntegrationIds?.length) {
        searchParams.extraIntegrationIds = params.extraIntegrationIds.join(',')
    }
    const res = apiCall<MarketplaceIntegration[]>(() =>
        http.get(url.toString(), {
            searchParams,
        }),
    );
    return res;
}

export async function disableSubscription(params: DisableSubscriptionParams) {
    const url = `marketplace/billing/disableSubscription`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: params }));
    return res;
}
export async function toggleSubscription(params: ToggleSubscriptionParams) {
    const url = `marketplace/billing/toggleSubscriptionStatus`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: params }));
    return res;
}

export async function disableIntegration(params: DisableIntegrationParams) {
    const url = `marketplace/billing/disableIntegration`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: params }));
    return res;
}

export async function activatePromocode(params: {projectId: string, promocode: string}) {
    const url = `marketplace/billing/activatePromocode`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: params }));
    return res;
}
export async function fillByInn(params: {inn: string}) {
    const url = `marketplace/invoice/fillByInn`;
    const res = apiCall<any>(() => http.get(url.toString(), { searchParams: params }));
    return res;
}

export async function searchByInn(params: {name: string}) {
    const url = `marketplace/invoice/searchByInn`;
    const res = apiCall<any>(() => http.get(url.toString(), { searchParams: params }));
    return res;
}

export async function updateContractor(params: {
    projectId: string
    inn: string
    kpp: string
    name: string
    addr: string
    tel?: string,
}) {
    const url = `marketplace/contractor/update`;
    const res = apiCall<any>(() => http.put(url.toString(), { json: params }));
    return res;
}

export async function createContractor(params: {
    projectId: string
    inn: string
    kpp: string
    name: string
    addr: string
    tel?: string,
}) {
    const url = `marketplace/contractor/create`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: params }));
    return res;
}

export async function deleteContractor(params: {
    projectId: string
    inn: string,
}) {
    const url = `marketplace/contractor/delete`;
    const res = apiCall<any>(() => http.delete(url.toString(), { json: params }));
    return res;
}

export async function getContractors(params: {projectId: string}) {
    const url = `marketplace/invoice/getProjectContractors`;
    const res = apiCall<any>(() => http.get(url.toString(), { searchParams: params }));
    return res;
}
export async function createInvoice(params: {
    inn: string
    kpp: string
    name: string
    addr: string
    price: string
    periodStart?: string
    period?: string
    projectId?: string
    paymentPayloadId?: string
    contractorExists?: boolean
    tariff?: string
    projName?: string
    invoiceItems?: Array<{
        type: 'tariff' | 'channel'
        title: string
        price: number
        quantity: number
        tariffName?: string
    }>
}) {
    const url = `marketplace/invoice/createInvoice`;
    try {
        const res = await http.post(url.toString(), { json: params });
        if (res.ok) {
            await downloadInvoiceDocument(res);
            return true;
        } else {
            store.dispatch("callNotify", "Ошибка при создании счёта");
        }
    } catch (error) {
        store.dispatch("callNotify", "Ошибка при создании счёта: " + error);
    }
    return false;
}

async function downloadInvoiceDocument(res: Response) {
    // Проверяем, есть ли предупреждение в заголовках
    const warningEncoded = res.headers.get("X-Invoice-Warning");

    if (warningEncoded) {
        // Декодируем base64 обратно в текст с правильной кодировкой UTF-8
        try {
            // Декодируем base64 в Uint8Array
            const binaryString = atob(warningEncoded);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            // Декодируем UTF-8
            const warning = new TextDecoder("utf-8").decode(bytes);

            // Показываем модальное окно с предупреждением
            const modalStore = useModalStore();
            modalStore.open({
                title: "ВНИМАНИЕ!",
                text: warning,
                color: "error",
            });
        } catch (e) {
            console.error("Ошибка декодирования предупреждения:", e);
        }
    }

    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "invoice.docx";
    document.body.appendChild(link); // Для Firefox
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
}

export async function getPaymentLink(params: { projectId: string; paymentPayloadId: number }) {
    const url = `marketplace/billing/paymentLink`
    return apiCall<{ token: string; url: string }>(() => http.get(url.toString(), { searchParams: params }))
}

// Страница оплаты по ссылке без входа: каждая ручка проверяет токен ссылки.
function paymentLinkPath(link: SpecialPaymentLink, action: string) {
    return `noAuth/payment/${encodeURIComponent(link.projectId)}/${link.paymentPayloadId}/${action}`
}

export async function getPaymentLinkOffer(link: SpecialPaymentLink) {
    return apiCall<any>(() =>
        http.get(paymentLinkPath(link, 'offer'), { searchParams: { t: link.token } })
    )
}

export async function payByPaymentLink(link: SpecialPaymentLink) {
    return apiCall<MarketplaceInstallResult>(() =>
        http.post(paymentLinkPath(link, 'card'), { searchParams: { t: link.token } })
    )
}

export async function searchByInnForPaymentLink(link: SpecialPaymentLink, params: { name: string }) {
    return apiCall<any>(() =>
        http.get(paymentLinkPath(link, 'searchByInn'), {
            searchParams: { t: link.token, name: params.name },
        })
    )
}

export async function createInvoiceByPaymentLink(
    link: SpecialPaymentLink,
    payer: { inn: string; kpp: string; name: string; addr: string }
) {
    const res = await http.post(paymentLinkPath(link, 'invoice'), {
        searchParams: { t: link.token },
        json: payer,
    })
    if (!res.ok) return false
    await downloadInvoiceDocument(res)
    return true
}


export async function getSubscriptions(projectId: string) {
    const url = `marketplace/billing/subscriptions`
    const res = apiCall<any>(() => http.get(url.toString(), { searchParams: { projectId } }))
    return res
}

export async function scheduleTariffChange(
    paymentPayloadId: number,
    projectId: string,
    retainedSlotIds?: string[],
) {
    const url = `marketplace/billing/scheduleTariffChange`;
    const payload: {
        paymentPayloadId: number
        projectId: string
        retainedSlotIds?: string[]
    } = { paymentPayloadId, projectId }
    if (retainedSlotIds !== undefined) {
        payload.retainedSlotIds = retainedSlotIds
    }
    const res = apiCall<{
        status?: string
        scheduledTariffChangeId?: number
        paymentUrl?: string
        alreadyScheduled?: boolean
    }>(() => http.post(url.toString(), { json: payload }));
    return res;
}
export async function cancelScheduleTariffChange(projectId: string) {
    const url = `marketplace/billing/cancelScheduledTariffChange`;
    const res = apiCall<any>(() => http.post(url.toString(), { json: { projectId } }));
    return res;
}

export interface DowngradeChannelSelectionChannel {
    connectorId?: string
    id?: string
    name?: string
    title?: string
    phone?: string
    type?: string
    slotType?: string
}

export interface DowngradeChannelSelectionSlot {
    slotId: string
    slotType?: string
    channel?: DowngradeChannelSelectionChannel | null
}

export interface DowngradeChannelSelectionSlotType {
    slotType: string
    /** Сколько каналов можно оставить: tariffCount + extraCount. */
    targetCount: number
    /** Ёмкость целевого тарифа. */
    tariffCount?: number
    /** Каналы, докупленные в том же заказе: лишние каналы переедут на них. */
    extraCount?: number
    slots?: DowngradeChannelSelectionSlot[]
    connectedCount?: number
    mustChoose: boolean
}

export interface DowngradeChannelSelectionResult {
    slotTypes?: DowngradeChannelSelectionSlotType[]
    notAffected?: DowngradeChannelSelectionChannel[]
}

export async function getDowngradeChannelSelection(params: {
    projectId: string
    integrationId: string
    extraChannels?: number
}) {
    const url = `marketplace/billing/downgradeChannelSelection`
    return apiCall<DowngradeChannelSelectionResult>(() =>
        http.post(url, { json: params })
    )
}

export async function setRetainedChannels(params: {
    projectId: string
    retainedSlotIds: string[]
}) {
    const url = `marketplace/billing/setRetainedChannels`
    return apiCall<any>(() => http.post(url, { json: params }))
}
export async function updatePaymentPayloadStatus({
    projectId,
    paymentPayloadId,
    status,
}: {
    projectId: string
    paymentPayloadId: number
    status: string
}) {
    const url = `marketplace/billing/updatePaymentPayloadStatus`
    const res = apiCall<any>(() =>
        http.post(url.toString(), { json: { projectId, paymentPayloadId, status } }),
    )
    return res
}

export async function paymentConfirmClick({
    projectId,
    paymentPayloadId,
}: {
    projectId: string
    paymentPayloadId: number
}) {
    const url = `marketplace/billing/paymentConfirmClick`
    return apiCall<any>(() =>
        http.post(url.toString(), { json: { projectId, paymentPayloadId } }),
    )
}

export async function isIntegrationInstalled({projectId, integrationId}: {projectId: string, integrationId: string}) {
    const url = `marketplace/integration/isInstalled`;
    const res = apiCall<{ isInstalled: boolean; status: string }>(() =>
        http.get(url.toString(), { searchParams: { projectId, integrationId } }),
    );
    return res;
}

export async function checkIntegrationInstalled(projectId: string, integrationId: string): Promise<boolean> {
    if (!projectId || !integrationId) return false
    const { data, error } = await isIntegrationInstalled({ projectId, integrationId })
    return !error && data?.isInstalled === true
}

export async function filterUninstalledPromos<T extends { marketplaceId: string }>(
    projectId: string,
    items: readonly T[],
): Promise<T[]> {
    if (!projectId) return [...items]

    const results = await Promise.all(
        items.map(async item => {
            const installed = await checkIntegrationInstalled(projectId, item.marketplaceId)
            return { item, installed }
        }),
    )

    return results.filter(result => !result.installed).map(result => result.item)
}

export async function getDetailedIntegrationData(
    projectId: string,
    integrationId: string,
): Promise<{ data: DetailedIntegrationData | null; error: null | any }> {
    const url = `marketplace/integration/${integrationId}`
    return apiCall<DetailedIntegrationData>(() =>
        http.get(url, { searchParams: { projectId } })
    )
}
